import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api, LEVEL_LABELS } from '@/lib/api';
import ShareButtons from '@/components/ShareButtons';
import { Swords, Briefcase, Share2 } from 'lucide-react';

export default function ChallengeResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.result;
  const challengeId = location.state?.challengeId;
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    if (!result || !challengeId) { navigate('/'); return; }
    api.get(`/challenge/${challengeId}`).then(r => setChallenge(r.data)).catch(() => {});
  }, [challengeId, result, navigate]);

  const [showShare, setShowShare] = useState(false);

  if (!result) return null;

  const myScore = result.correctCount;
  const myTotal = result.totalQuestions;
  const challengerScore = challenge?.challengerCorrect || 0;
  const challengerTotal = challenge?.challengerTotal || myTotal;
  const challengerName = challenge?.displayName || 'Herausforderer';
  const iWon = myScore > challengerScore;
  const isDraw = myScore === challengerScore;

  return (
    <div className="flex-1 flex items-center justify-center px-4" data-testid="challenge-result-page">
      <div className="animate-fade-up max-w-lg w-full">
        <div className="glass-card rounded-2xl p-8 text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mb-4">
            <Swords className="w-8 h-8 text-[#A78BFA]" />
          </div>
          <p className="text-[#9CA3AF] text-sm mb-2">{LEVEL_LABELS[result.level]} Challenge</p>
          <h2 className="font-outfit font-bold text-2xl text-white mb-6">
            {isDraw ? 'Unentschieden!' : iWon ? 'Du hast gewonnen!' : `${challengerName} war besser!`}
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-xs text-[#9CA3AF] mb-2">Du</p>
              <p className="text-3xl font-outfit font-bold text-white">{myScore}/{myTotal}</p>
              <p className="text-sm text-[#9CA3AF]">{result.percentCorrect}%</p>
            </div>
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-xs text-[#9CA3AF] mb-2">{challengerName}</p>
              <p className="text-3xl font-outfit font-bold text-white">{challengerScore}/{challengerTotal}</p>
              <p className="text-sm text-[#9CA3AF]">{challengerTotal > 0 ? Math.round(challengerScore / challengerTotal * 100) : 0}%</p>
            </div>
          </div>

          <p className="text-xs text-[#4B5563]">Challenges zählen nicht ins Ranking</p>
        </div>

        <div className="grid gap-3">
          <Button
            onClick={() => navigate('/apply/type', { state: { runId: location.state?.runId, result } })}
            className="w-full h-12 bg-[#10B981] hover:bg-[#059669] text-white font-outfit font-bold rounded-xl"
            data-testid="apply-btn"
          >
            <Briefcase className="w-4 h-4 mr-2" /> Bewerben
          </Button>
          <Button
            onClick={() => setShowShare(!showShare)}
            variant="outline"
            className="w-full h-12 border-[#8B5CF6]/40 text-[#A78BFA] hover:bg-[#8B5CF6]/10 font-outfit font-bold rounded-xl"
            data-testid="share-challenge-btn"
          >
            <Share2 className="w-4 h-4 mr-2" /> Freunde herausfordern
          </Button>
          {showShare && (
            <div className="glass-card rounded-xl p-4 animate-fade-up">
              <ShareButtons runId={location.state?.runId} />
            </div>
          )}
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="w-full h-12 border-white/10 text-[#9CA3AF] hover:bg-white/5 font-outfit rounded-xl"
            data-testid="own-quiz-btn"
          >
            Eigenes Quiz starten
          </Button>
        </div>
      </div>
    </div>
  );
}
